/**
 * feAI – Frontend
 * src/pages/LoginPage.tsx
 *
 * Orchestrates the multi-step login flow:
 *
 *   [SSO params in URL] → redirect to /sso-auth (dedicated SSO flow)
 *   [idle / loading]    → <LoginForm>      (Step 1)
 *   [multi_account]     → <ProfilePicker>  (Step 2 – conditional)
 *   [must_reset]        → redirect to /forgot-password (forced reset)
 *   [authenticated]     → redirect to /dashboard
 */

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoginForm }     from '../components/auth/LoginForm';
import { ProfilePicker } from '../components/auth/ProfilePicker';
import '../styles/login.css';

export const LoginPage: React.FC = () => {
  const { step, login, selectProfile, logout, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── SSO auto-login from legacy server ─────────────────────────────────────
  // Se l'URL contiene parametri SSO, delega tutto il flusso a /sso-auth
  // (percorso dedicato con retry automatico e gestione errori).
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    const ssoCodAgente = searchParams.get('sso_cod_agente');
    const ssoUsername = searchParams.get('sso_username');
    const ssoTs = searchParams.get('sso_ts');

    if (ssoToken && ssoCodAgente && ssoUsername && ssoTs) {
      const params = new URLSearchParams({
        sso_token: ssoToken,
        sso_cod_agente: ssoCodAgente,
        sso_username: ssoUsername,
        sso_ts: ssoTs,
      });
      navigate(`/sso-auth?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // ── Error message: SSO error passed from /sso-auth via query string ──────
  // SsoAuthPage usa una propria istanza di useAuth, quindi il messaggio di
  // errore non è condiviso: lo passiamo via URL (parametro sso_error).
  const effectiveError = error ?? searchParams.get('sso_error');

  // ── Redirect after successful auth ────────────────────────────────────────
  useEffect(() => {
    if (step.type === 'authenticated') {
      navigate('/', { replace: true });
      return;
    }

    // Redirect forzato a /forgot-password con info canale
    if (step.type === 'must_reset') {
      const params = new URLSearchParams({ force: '1' });
      if (step.channel)           params.set('channel', step.channel);
      if (step.maskedDestination) params.set('dest', step.maskedDestination);
      navigate(`/forgot-password?${params.toString()}`, { replace: true });
    }
  }, [step, navigate]);

  const isLoading = step.type === 'loading';

  const cardClass = [
    'login-card',
    step.type === 'multi_account' ? 'profile-picker-card' : '',
  ].filter(Boolean).join(' ');

  return (
    <main className="login-page" aria-label="Pagina di accesso feAI">
      <div className={cardClass}>

        {/* ── Step 1: Login form ──────────────────────────────────────────── */}
        {(step.type === 'idle' || step.type === 'loading') && (
          <LoginForm
            onSubmit={login}
            isLoading={isLoading}
            error={effectiveError}
          />
        )}

        {/* ── Step 2: Multi-account profile picker ────────────────────────── */}
        {step.type === 'multi_account' && (
          <ProfilePicker
            profiles={step.profiles}
            onSelect={selectProfile}
            onBack={logout}
            isLoading={isLoading}
            error={effectiveError}
          />
        )}

      </div>
    </main>
  );
};
